import { useState, useEffect } from "react";
import { CalendarProvider } from "./calendar-context";
import CalendarHeader from "./calendar-header";
import DailyView from "./views/daily-view";
import WeeklyView from "./views/weekly-view";
import MonthlyView from "./views/monthly-view";
import SelectionPanel from "./selection-panel";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type {
  ViewType,
  Event,
  TimeRange,
  BookedTimeRange,
} from "@/lib/calendar-types";
import {
  generateInitialEvents,
  generateBookedTimeRanges,
} from "@/lib/calendar-utils";

interface CalendarProps {
  initialBookedRanges: BookedTimeRange[];
  initialUserDefinedRanges: any[];
  onBookTimeSlot: (booking: BookedTimeRange) => void;
  onTimeSlotSelect: (timeRange: TimeRange) => void;
  slotDuration: number;
  selectedTimeSlots: TimeRange[];
  onRangesChange?: (ranges: TimeRange[]) => void;
  selectionMode?: "click-only" | "drag";
  onSlotClick?: (slot: Date) => void;
  enforceRangeRestriction?: boolean; // New flag to control whether to enforce outside-range restrictions
}

export default function Calendar({
  initialBookedRanges,
  initialUserDefinedRanges,
  onBookTimeSlot,
  onTimeSlotSelect,
  slotDuration,
  selectedTimeSlots: externalSelectedTimeSlots,
  onRangesChange,
  selectionMode = "drag",
  onSlotClick,
  enforceRangeRestriction = false, // Default to false for backward compatibility
}: CalendarProps) {
  const [view, setView] = useState<ViewType>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [bookedRanges, setBookedRanges] =
    useState<BookedTimeRange[]>(initialBookedRanges);
  // Initialize selectedTimeSlots with both external slots and initial user-defined ranges
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeRange[]>([
    ...(externalSelectedTimeSlots || []),
    ...(initialUserDefinedRanges || []),
  ]);
  const { toast } = useToast();

  useEffect(() => {
    // Load sample events for demonstration
    setEvents(generateInitialEvents());
  }, []);

  const handleViewChange = (newView: ViewType) => {
    setView(newView);
  };

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() - 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }

    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() + 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEditEvent = (event: Event) => {
    // For this implementation, we're focusing on time slot selection only
    // Event editing functionality is preserved but not the focus
  };

  const showConflictToast = (message: string) => {
    toast({
      title: "Booking Conflict",
      description: message,
      variant: "destructive",
    });
  };

  const handleAddTimeRange = (range: TimeRange) => {
    // Ensure range dates are Date objects
    const rangeStartDate =
      range.startDate instanceof Date
        ? range.startDate
        : new Date(range.startDate);
    const rangeEndDate =
      range.endDate instanceof Date ? range.endDate : new Date(range.endDate);

    // Normalize the range object
    const normalizedRange = {
      ...range,
      startDate: rangeStartDate,
      endDate: rangeEndDate,
    };

    // Check if the slot is already selected - if so, we're removing it
    const isSlotAlreadySelected = selectedTimeSlots.some((existingRange) => {
      // Ensure dates are Date objects
      const existingStartDate =
        existingRange.startDate instanceof Date
          ? existingRange.startDate
          : new Date(existingRange.startDate);
      const existingEndDate =
        existingRange.endDate instanceof Date
          ? existingRange.endDate
          : new Date(existingRange.endDate);

      // Check if the range is fully contained within an existing range
      // or if it overlaps with an existing range (for drag deselection)
      return (
        ((rangeStartDate.getTime() >= existingStartDate.getTime() &&
          rangeEndDate.getTime() <= existingEndDate.getTime()) ||
          (rangeStartDate.getTime() < existingEndDate.getTime() &&
            rangeEndDate.getTime() > existingStartDate.getTime())) &&
        existingRange.dayName === range.dayName &&
        format(rangeStartDate, "yyyy-MM-dd") ===
          format(existingStartDate, "yyyy-MM-dd")
      );
    });

    if (isSlotAlreadySelected) {
      // We're removing a slot from an existing selection
      // Find all ranges that contain or overlap with this slot
      const affectedRanges = selectedTimeSlots.filter((existingRange) => {
        // Ensure dates are Date objects
        const existingStartDate =
          existingRange.startDate instanceof Date
            ? existingRange.startDate
            : new Date(existingRange.startDate);
        const existingEndDate =
          existingRange.endDate instanceof Date
            ? existingRange.endDate
            : new Date(existingRange.endDate);

        // Check for containment or overlap
        return (
          ((rangeStartDate.getTime() >= existingStartDate.getTime() &&
            rangeEndDate.getTime() <= existingEndDate.getTime()) ||
            (rangeStartDate.getTime() < existingEndDate.getTime() &&
              rangeEndDate.getTime() > existingStartDate.getTime())) &&
          existingRange.dayName === range.dayName
        );
      });

      // Create a copy of the current ranges that we'll modify
      let updatedRanges = selectedTimeSlots.filter(
        (range) =>
          !affectedRanges.some(
            (affectedRange) => affectedRange.id === range.id,
          ),
      );

      // Process each affected range
      affectedRanges.forEach((affectedRange) => {
        // Check if we need to create a range before the deselection
        const affectedStartDate =
          affectedRange.startDate instanceof Date
            ? affectedRange.startDate
            : new Date(affectedRange.startDate);

        if (affectedStartDate.getTime() < rangeStartDate.getTime()) {
          const beforeRange: TimeRange = {
            id: crypto.randomUUID(),
            startDate: affectedStartDate,
            endDate: rangeStartDate,
            dayName: affectedRange.dayName,
          };
          updatedRanges.push(beforeRange);
        }

        // Check if we need to create a range after the deselection
        const affectedEndDate =
          affectedRange.endDate instanceof Date
            ? affectedRange.endDate
            : new Date(affectedRange.endDate);

        if (affectedEndDate.getTime() > rangeEndDate.getTime()) {
          const afterRange: TimeRange = {
            id: crypto.randomUUID(),
            startDate: rangeEndDate,
            endDate: affectedEndDate,
            dayName: affectedRange.dayName,
          };
          updatedRanges.push(afterRange);
        }
      });

      setSelectedTimeSlots(updatedRanges);
      onRangesChange?.(updatedRanges);
      return;
    }

    // If we're here, we're adding a new slot, not removing one
    // Check if the new range conflicts with any booked ranges
    const conflictingBookedRanges = bookedRanges.filter((bookedRange) => {
      // Ensure dates are Date objects
      const bookedStartDate =
        bookedRange.startDate instanceof Date
          ? bookedRange.startDate
          : new Date(bookedRange.startDate);
      const bookedEndDate =
        bookedRange.endDate instanceof Date
          ? bookedRange.endDate
          : new Date(bookedRange.endDate);

      return (
        bookedStartDate.getTime() < rangeEndDate.getTime() &&
        bookedEndDate.getTime() > rangeStartDate.getTime() &&
        bookedRange.dayName === range.dayName
      );
    });

    if (conflictingBookedRanges.length > 0) {
      // If there's a conflict with a booked range, show a toast and don't add the range
      showConflictToast(
        `Cannot select this time range as it conflicts with ${conflictingBookedRanges.length} booked appointment(s).`,
      );
      return;
    }

    // Find all ranges that overlap with the new range
    const overlappingRanges = selectedTimeSlots.filter((existingRange) => {
      // Ensure dates are Date objects
      const existingStartDate =
        existingRange.startDate instanceof Date
          ? existingRange.startDate
          : new Date(existingRange.startDate);
      const existingEndDate =
        existingRange.endDate instanceof Date
          ? existingRange.endDate
          : new Date(existingRange.endDate);

      return (
        existingStartDate.getTime() < rangeEndDate.getTime() &&
        existingEndDate.getTime() > rangeStartDate.getTime() &&
        existingRange.dayName === range.dayName &&
        format(rangeStartDate, "yyyy-MM-dd") ===
          format(existingStartDate, "yyyy-MM-dd")
      );
    });

    let updatedRanges: TimeRange[];

    if (overlappingRanges.length === 0) {
      // If no overlap, simply add the new range
      updatedRanges = [...selectedTimeSlots, normalizedRange];
    } else {
      // Create a copy of the current ranges that we'll modify
      updatedRanges = selectedTimeSlots.filter(
        (r) => !overlappingRanges.some((overlap) => overlap.id === r.id),
      );

      // Process each overlapping range
      overlappingRanges.forEach((overlappingRange) => {
        // Check if we need to create a range before the new range
        const overlappingStartDate =
          overlappingRange.startDate instanceof Date
            ? overlappingRange.startDate
            : new Date(overlappingRange.startDate);

        if (overlappingStartDate.getTime() < rangeStartDate.getTime()) {
          const beforeRange: TimeRange = {
            id: crypto.randomUUID(),
            startDate: overlappingStartDate,
            endDate: rangeStartDate,
            dayName: overlappingRange.dayName,
          };
          updatedRanges.push(beforeRange);
        }

        // Check if we need to create a range after the new range
        const overlappingEndDate =
          overlappingRange.endDate instanceof Date
            ? overlappingRange.endDate
            : new Date(overlappingRange.endDate);

        if (overlappingEndDate.getTime() > rangeEndDate.getTime()) {
          const afterRange: TimeRange = {
            id: crypto.randomUUID(),
            startDate: rangeEndDate,
            endDate: overlappingEndDate,
            dayName: overlappingRange.dayName,
          };
          updatedRanges.push(afterRange);
        }
      });

      // Always add the new range when we're not removing a slot
      updatedRanges.push(normalizedRange);
    }

    // Update the state and notify parent
    setSelectedTimeSlots(updatedRanges);
    onRangesChange?.(updatedRanges);
  };

  const handleRemoveTimeRange = (rangeId: string) => {
    // Prevent multiple executions for the same ID in quick succession
    const updatedRanges = selectedTimeSlots.filter(
      (range) => range.id !== rangeId,
    );

    // Update local state first
    setSelectedTimeSlots(updatedRanges);

    // Notify parent component immediately
    if (onRangesChange) {
      onRangesChange(updatedRanges);
    }
  };

  const handleClearAllSelections = () => {
    // Update local state
    setSelectedTimeSlots([]);

    // Notify parent component immediately
    if (onRangesChange) {
      onRangesChange([]);
    }
  };

  // Add a useEffect to merge adjacent ranges and handle overlapping ranges after selections change
  useEffect(() => {
    // Only process ranges if there are at least 2 ranges
    if (selectedTimeSlots.length >= 2) {
      // First handle overlapping ranges
      const nonOverlappingRanges = handleOverlappingRanges(selectedTimeSlots);

      // Then merge adjacent ranges
      const mergedRanges = mergeAdjacentRanges(nonOverlappingRanges);

      // Only update if the processing actually changed the ranges
      if (
        mergedRanges.length !== selectedTimeSlots.length ||
        JSON.stringify(mergedRanges) !== JSON.stringify(selectedTimeSlots)
      ) {
        onRangesChange?.(mergedRanges);
      }
    }
  }, [selectedTimeSlots]);

  // Handle overlapping ranges by merging them
  const handleOverlappingRanges = (ranges: TimeRange[]): TimeRange[] => {
    if (ranges.length <= 1) return ranges;

    // Sort ranges by start time, ensuring startDate is a Date object
    const sortedRanges = [...ranges].sort((a, b) => {
      const aStartDate =
        a.startDate instanceof Date ? a.startDate : new Date(a.startDate);
      const bStartDate =
        b.startDate instanceof Date ? b.startDate : new Date(b.startDate);
      return aStartDate.getTime() - bStartDate.getTime();
    });

    const result: TimeRange[] = [];
    let currentRange = sortedRanges[0];

    for (let i = 1; i < sortedRanges.length; i++) {
      const nextRange = sortedRanges[i];

      // Skip ranges on different days or with different dates
      const currentStartDate =
        currentRange.startDate instanceof Date
          ? currentRange.startDate
          : new Date(currentRange.startDate);
      const nextStartDate =
        nextRange.startDate instanceof Date
          ? nextRange.startDate
          : new Date(nextRange.startDate);

      if (
        currentRange.dayName !== nextRange.dayName ||
        format(currentStartDate, "yyyy-MM-dd") !==
          format(nextStartDate, "yyyy-MM-dd")
      ) {
        result.push(currentRange);
        currentRange = nextRange;
        continue;
      }

      // Check for overlap
      const currentEndDate =
        currentRange.endDate instanceof Date
          ? currentRange.endDate
          : new Date(currentRange.endDate);

      if (currentEndDate.getTime() > nextStartDate.getTime()) {
        // Merge overlapping ranges
        currentRange = {
          ...currentRange,
          id: crypto.randomUUID(), // Generate new ID for merged range
          endDate: new Date(
            Math.max(
              currentEndDate.getTime(),
              (nextRange.endDate instanceof Date
                ? nextRange.endDate
                : new Date(nextRange.endDate)
              ).getTime(),
            ),
          ),
        };
      } else {
        // No overlap, add current range and move to next
        result.push(currentRange);
        currentRange = nextRange;
      }
    }

    // Add the last range
    result.push(currentRange);
    return result;
  };

  const mergeAdjacentRanges = (ranges: TimeRange[]): TimeRange[] => {
    if (ranges.length <= 1) return ranges;

    // Sort ranges by start time, ensuring startDate is a Date object
    const sortedRanges = [...ranges].sort((a, b) => {
      const aStartDate =
        a.startDate instanceof Date ? a.startDate : new Date(a.startDate);
      const bStartDate =
        b.startDate instanceof Date ? b.startDate : new Date(b.startDate);
      return aStartDate.getTime() - bStartDate.getTime();
    });

    const mergedRanges: TimeRange[] = [];
    let currentRange = sortedRanges[0];

    for (let i = 1; i < sortedRanges.length; i++) {
      const nextRange = sortedRanges[i];

      // Check if ranges are adjacent and on the same day and same date
      const currentEndDate =
        currentRange.endDate instanceof Date
          ? currentRange.endDate
          : new Date(currentRange.endDate);
      const nextStartDate =
        nextRange.startDate instanceof Date
          ? nextRange.startDate
          : new Date(nextRange.startDate);

      const currentStartDate =
        currentRange.startDate instanceof Date
          ? currentRange.startDate
          : new Date(currentRange.startDate);

      if (
        currentEndDate.getTime() === nextStartDate.getTime() &&
        currentRange.dayName === nextRange.dayName &&
        format(currentStartDate, "yyyy-MM-dd") ===
          format(nextStartDate, "yyyy-MM-dd")
      ) {
        // Merge ranges
        currentRange = {
          ...currentRange,
          endDate: nextRange.endDate,
        };
      } else {
        // Add current range to merged ranges and move to next range
        mergedRanges.push(currentRange);
        currentRange = nextRange;
      }
    }

    // Add the last range
    mergedRanges.push(currentRange);

    return mergedRanges;
  };

  // Update selectedTimeSlots when externalSelectedTimeSlots changes
  useEffect(() => {
    // Make a deep copy to ensure we're not working with references
    const externalSlots = externalSelectedTimeSlots
      ? JSON.parse(JSON.stringify(externalSelectedTimeSlots))
      : [];

    setSelectedTimeSlots(externalSlots);
  }, [externalSelectedTimeSlots]);

  return (
    <CalendarProvider
      value={{
        view,
        currentDate,
        events,
        selectedRanges: selectedTimeSlots,
        bookedRanges,
        selectionMode,
        enforceRangeRestriction,
        onViewChange: handleViewChange,
        onDateChange: handleDateChange,
        onEditEvent: handleEditEvent,
        onAddTimeRange: handleAddTimeRange,
        onRemoveTimeRange: handleRemoveTimeRange,
        onClearAllSelections: handleClearAllSelections,
        showConflictToast,
        onSlotClick,
      }}
    >
      <div
        className="grid md:grid-cols-[1fr_350px] gap-4"
        data-testid="calendar-container"
      >
        <div className="border rounded-lg shadow bg-background">
          <CalendarHeader
            view={view}
            currentDate={currentDate}
            onViewChange={handleViewChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onToday={handleToday}
          />

          <div className="calendar-body">
            {view === "day" && <DailyView />}
            {view === "week" && <WeeklyView />}
            {view === "month" && <MonthlyView />}
          </div>
        </div>

        <SelectionPanel
          selectedRanges={selectedTimeSlots}
          bookedRanges={bookedRanges}
          onRemoveRange={handleRemoveTimeRange}
          onClearAll={handleClearAllSelections}
        />
      </div>
    </CalendarProvider>
  );
}
